/**
 * 답형식별 정답률 차트 컴포넌트
 * Recharts BarChart를 사용하여 M/S/B/N/E/T 타입별 정답률 표시
 */

import React from 'react';
import { Card } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { AnswerTypeStatItem } from '../../types/stats';

interface AnswerTypeChartProps {
  answerTypes: AnswerTypeStatItem[];
  loading?: boolean;
}

/**
 * 답형식별 색상 매핑
 */
const ANSWER_TYPE_COLORS: Record<string, string> = {
  M: '#1890ff', // 파란색
  S: '#52c41a', // 녹색
  B: '#faad14', // 주황색
  N: '#722ed1', // 보라색
  E: '#13c2c2', // 청록색 (엄격)
  F: '#9254de', // 보라-파랑 (유연)
  T: '#eb2f96', // 마젠타
};

/**
 * 답형식 한글 이름 매핑
 */
const ANSWER_TYPE_LABELS: Record<string, string> = {
  M: '객관식',
  S: '보기선택',
  B: 'OX',
  N: '숫자',
  E: '수식(엄격)',
  F: '수식(유연)',
  T: '서술형',
};

export const AnswerTypeChart: React.FC<AnswerTypeChartProps> = ({
  answerTypes,
  loading = false,
}) => {
  // 차트 데이터 변환
  const chartData = answerTypes.map((item) => ({
    type: item.answer_type,
    name: ANSWER_TYPE_LABELS[item.answer_type] || item.answer_type,
    정답률: item.accuracy,
    정답수: item.correct_problems,
    문제수: item.total_problems,
  }));

  return (
    <Card title="답형식별 정답률" loading={loading}>
      {chartData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          데이터가 없습니다
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              domain={[0, 100]}
              label={{ value: '정답률 (%)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === '정답률') {
                  return [`${value.toFixed(1)}%`, name];
                }
                return [value, name];
              }}
            />
            <Legend />
            <Bar dataKey="정답률" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={ANSWER_TYPE_COLORS[entry.type] || '#8884d8'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};
